import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faBars,
} from '@fortawesome/pro-regular-svg-icons';
import { faBarsSort } from '@fortawesome/pro-regular-svg-icons';
import { SidebarMenuComponent } from '@shared/sidebar-menu/sidebar-menu.component';
import { SidebarProfileComponent } from '@shared/sidebar-profile/sidebar-profile.component';
import { SidebarBackofficeComponent } from '@shared/sidebar-backoffice/sidebar-backoffice.component';
import { TopNavbarComponent } from '@shared/top-navbar/top-navbar.component';
import { ProfileMenuComponent } from '@shared/profile-menu/profile-menu.component';
import { SidebarNavigationGroupComponent } from '@shared/sidebar-navigation/sidebar-navigation-group.component';
import {
  DASHBOARD_FOOTER_GROUP,
  DASHBOARD_PRIMARY_GROUP,
} from '@shared/sidebar-navigation/sidebar-navigation.config';
import { SearchChatComponent } from '@shared/search-chat/search-chat.component';
import { SupportChatComponent } from '@shared/support-chat/support-chat.component';
import { AlertComponent } from '@shared/alert/alert.component';
import { BetaNoticeModalComponent } from '@shared/beta-notice-modal/beta-notice-modal.component';
import { TrialStatusBadgeComponent } from '@shared/trial-status-badge/trial-status-badge.component';
import { DashboardShellFacade } from './dashboard-shell.facade';
import { DashboardBackofficeEntryService } from './dashboard-backoffice-entry.service';
import { OnboardingTourService } from '@core/services/onboarding-tour.service';
import { BetaNoticeService } from '@core/services/beta-notice.service';
import { LOWEST_PLAN_WITH_ALARMS } from '@core/config/company-plans.config';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  providers: [DashboardShellFacade, DashboardBackofficeEntryService],
  imports: [
    SidebarMenuComponent,
    SidebarProfileComponent,
    SidebarBackofficeComponent,
    NgClass,
    FontAwesomeModule,
    SearchChatComponent,
    SupportChatComponent,
    TopNavbarComponent,
    ProfileMenuComponent,
    SidebarNavigationGroupComponent,
    AlertComponent,
    BetaNoticeModalComponent,
    TrialStatusBadgeComponent,
    RouterOutlet,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit, OnDestroy {
  readonly dashboardFooterGroup = DASHBOARD_FOOTER_GROUP;
  readonly supportChatCustomPosition: Record<string, string> = {
    right: '1.5rem',
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 6.5rem)',
  };

  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  faBarsSort = faBarsSort;
  faBars = faBars;

  constructor(
    public shell: DashboardShellFacade,
    public onboardingTourService: OnboardingTourService,
    public betaNotice: BetaNoticeService,
  ) {}

  ngOnInit(): void {
    this.shell.init();
    this.onboardingTourService.init();
  }

  ngOnDestroy(): void {
    this.shell.destroy();
  }

  private primaryGroupCache: { key: string; group: typeof DASHBOARD_PRIMARY_GROUP } | null = null;

  /**
   * "Clientes" solo para rol admin; "Alertas" con candado si el plan no la
   * incluye. Memoizado: devolver un objeto nuevo en cada ciclo de detección
   * provoca ExpressionChangedAfterItHasBeenChecked en desarrollo.
   */
  get dashboardPrimaryGroup(): typeof DASHBOARD_PRIMARY_GROUP {
    const key = `${this.shell.isSuperadmin}|${this.shell.isAlertsLocked}`;
    if (this.primaryGroupCache?.key === key) {
      return this.primaryGroupCache.group;
    }

    const minPlan = LOWEST_PLAN_WITH_ALARMS.label;
    const items = DASHBOARD_PRIMARY_GROUP.items
      .filter((item) => item.action !== 'clients' || this.shell.isSuperadmin)
      .map((item) =>
        item.action === 'alerts' && this.shell.isAlertsLocked
          ? { ...item, lockedBadge: minPlan, title: `${item.label} · Disponible desde el plan ${minPlan}` }
          : item,
      );

    const group = { ...DASHBOARD_PRIMARY_GROUP, items };
    this.primaryGroupCache = { key, group };
    return group;
  }
}
